/**
 * 閲覧セッション ID（EP-6）。
 *
 * **Cookie も localStorage も使わない**。タブ単位の `sessionStorage` に置き、タブを閉じれば
 * 消える。恒久的な識別子を端末へ新しく残さないための決めで、収集が既定 ON である以上、
 * 保存物は少ないほどよい（外部送信情報一覧に書くべきことも減る）。
 *
 * GA4 の慣行に合わせ、最後の送信から 30 分が空いたら別のセッションとして振り直す。
 * サーバはこの値をそのまま保存せず、署名鍵で HMAC してから主キーにする。
 */
import { VIEW_SESSION_KEY } from '../brand';

/** 無操作でセッションを切る時間（GA4 慣行の 30 分）。滞在時間の上限クリップにも使う。 */
export const SESSION_IDLE_MS = 30 * 60 * 1000;

interface Entry {
  id: string;
  /** 最後に送信した時刻（unix ミリ秒）。 */
  last: number;
}

type Book = Record<string, Entry>;

/**
 * ブック単位に閲覧セッション ID を持つ。
 *
 * 同じタブで別のブックを開けば別セッションになる（サーバの `view_sessions` も
 * 1 セッション 1 ブックなので、そこと形が揃う）。
 */
export class SessionId {
  private readonly memory: Book = {};

  constructor(private readonly view: Window) {}

  /**
   * 現在の ID を返し、最終送信時刻を進める。30 分以上空いていれば新しい ID を振る。
   *
   * @param now 現在時刻（テストから注入する）
   */
  current(book: string, now: number = Date.now()): string {
    const store = this.read();
    const entry = store[book];
    const fresh = entry !== undefined && now - entry.last < SESSION_IDLE_MS;
    const id = fresh ? entry.id : randomId(this.view);

    store[book] = { id, last: now };
    this.write(store);
    return id;
  }

  /** そのブックのセッションがまだ 1 度も送っていないか（流入元を載せる判断に使う）。 */
  isNew(book: string, now: number = Date.now()): boolean {
    const entry = this.read()[book];
    return entry === undefined || now - entry.last >= SESSION_IDLE_MS;
  }

  private read(): Book {
    const raw = this.storage()?.getItem(VIEW_SESSION_KEY);
    if (raw === null || raw === undefined) return { ...this.memory };
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const out: Book = {};
      for (const [book, value] of Object.entries(parsed)) {
        const entry = value as Partial<Entry>;
        if (typeof entry?.id === 'string' && typeof entry.last === 'number') {
          out[book] = { id: entry.id, last: entry.last };
        }
      }
      return out;
    } catch {
      // 壊れた保存内容でも計測を止めない
      return { ...this.memory };
    }
  }

  private write(store: Book): void {
    // sessionStorage が使えない環境（プライベートモード・容量超過・iframe の制限）では
    // メモリ上の状態だけで続ける。リロードで ID が変わるだけで、計測自体は動く。
    Object.assign(this.memory, store);
    try {
      this.storage()?.setItem(VIEW_SESSION_KEY, JSON.stringify(store));
    } catch {
      /* メモリへ縮退 */
    }
  }

  private storage(): Storage | null {
    try {
      return this.view.sessionStorage;
    } catch {
      return null;
    }
  }
}

/**
 * 128bit の乱数を 32 桁 hex で作る。
 *
 * `crypto.randomUUID` は**セキュア文脈でしか生えない**（平文 http:// の導入先では
 * undefined）ため、`getRandomValues` へのフォールバックを必ず持つ。
 */
export function randomId(view: Window): string {
  const crypto = view.crypto as Crypto | undefined;
  const uuid = crypto?.randomUUID?.();
  if (typeof uuid === 'string') {
    return uuid.replace(/-/g, '');
  }
  const bytes = new Uint8Array(16);
  if (crypto?.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // crypto そのものが無い環境（古い WebView 等）。統計の粒度を保つための最後の手段で、
    // ここで作る ID は推測されうるが、この値で守っているものは何も無い。
    for (let i = 0; i < bytes.length; i += 1)
      bytes[i] = Math.floor(Math.random() * 256);
  }
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}
